import { atom, Getter, Setter, PrimitiveAtom, WritableAtom } from 'jotai';
import { atomWithStorage, createJSONStorage, loadable } from 'jotai/utils';
import request from 'lib/request';

export type ResourceCacheAtomPayload<Response> =
  | {
      href: string;
      force?: boolean;
      data?: Response;
      abortController?: AbortController;
    }
  | { resetCache: true };

export type CacheMapEntry<Response> = {
  loading: boolean;
  data: Response | { error: string } | undefined;
};

export type ResourceCacheMapAtom<Response> = WritableAtom<
  Record<string, CacheMapEntry<Response> | undefined>,
  [ResourceCacheAtomPayload<Response>],
  void
>;

export type CacheMapAtom<Response> = PrimitiveAtom<
  Record<string, CacheMapEntry<Response>>
>;

// The atom shape returned by `createResourceAtom` — a read/write atom whose value
// is the resource's loading/data envelope and whose setter refetches. Exposed so
// callers can name the atom type without a `typeof createResourceAtom<T>`
// instantiation expression (which our prettier parser can't handle).
export type ResourceAtom<Response> = WritableAtom<
  CacheMapEntry<Response>,
  [],
  Promise<void>
>;

const createResourceCacheMapAtom = <
  Response,
>(): ResourceCacheMapAtom<Response> => {
  const mapBaseAtom = atom<Record<string, CacheMapEntry<Response> | undefined>>(
    {}
  );
  const mapAtom = atom<
    Record<string, CacheMapEntry<Response> | undefined>,
    [ResourceCacheAtomPayload<Response>],
    void
  >(
    (get) => get(mapBaseAtom),
    async (get, set, payload) => {
      if ('resetCache' in payload) {
        set(mapBaseAtom, {});
        return;
      }

      if (payload.data) {
        set(mapBaseAtom, (current) => ({
          ...current,
          [payload.href]: { loading: false, data: payload.data },
        }));
        return;
      }

      const href = payload.href;
      if (!href) {
        return;
      }
      const detail = get(mapBaseAtom)[payload.href];
      if (!detail || payload.force) {
        set(mapBaseAtom, (current) => ({
          ...current,
          [payload.href]: { loading: true, data: undefined },
        }));
        const response = await request.get<Response>(
          href,
          payload.abortController
        );

        set(mapBaseAtom, (current) => ({
          ...current,
          [payload.href]: {
            loading: false,
            data: response,
          },
        }));
      }
    }
  );
  return mapAtom;
};

const createSingleCacheEntryAtom =
  <T>(cache: ResourceCacheMapAtom<T>) =>
  (href: string) => {
    const singleEntryAtom = atom<
      CacheMapEntry<T>,
      [ResourceCacheAtomPayload<T>],
      void
    >(
      (get) => {
        return get(cache)[href] || { loading: true, data: undefined };
      },
      (_get, set, payload) => {
        set(cache, { ...payload });
      }
    );
    singleEntryAtom.onMount = (set) => {
      set({ href });
    };
    return singleEntryAtom;
  };

// Module-level seed store for resources, keyed by href. Populated only from tests
// (via `utils/test-utils/seed-resource`) so a `createResourceAtom` can hydrate its
// initial value synchronously — the same trick `rootResourceAtom` uses with
// `rootStore` to render on the first commit instead of waiting on the network.
//
// Production never writes to this map, so `resourceSeedStore.get(href)` is always
// `undefined` in the app and the atom starts `{ loading: true }` exactly as before.
const resourceSeedStore = new Map<string, unknown>();

/**
 * Pre-seed a resource so `createResourceAtom(href)` starts resolved.
 *
 * `createResourceAtom` normally starts every resource at `{ loading: true }` and
 * fetches over the network on mount. Under CI oversubscription those async hops can
 * push a page test's first `findBy`/`waitFor` past testing-library's window, making
 * the asserted element flaky-not-found. Seeding the store here lets the atom read its
 * value synchronously on the first commit — the same mechanism `rootStore` gives
 * `rootResourceAtom`. This does not weaken any assertion: the seeded payload is the
 * same object the corresponding MSW handler returns.
 *
 * TEST-ONLY. Production code never calls this, so the seed map stays empty in the app.
 *
 * @param {string} href - The resource href, exactly as passed to `createResourceAtom`.
 * @param {unknown} data - The payload the resource should resolve to.
 * @return {void}
 */
const seedResource = (href: string, data: unknown): void => {
  resourceSeedStore.set(href, data);
};

/**
 * Clear seeded resources so a seed from one test does not leak into the next.
 *
 * The seed map is a module-level singleton shared across tests in a worker; call in
 * `afterEach` to restore the un-seeded state so tests relying on the network behave
 * as before. TEST-ONLY.
 *
 * @return {void}
 */
const clearSeededResources = (): void => {
  resourceSeedStore.clear();
};

// refetchOptions: If a resource is refetched, by default we clear the old data,
// but we allow clients to configure this behavior if what they want is to keep arround
// the old data while the new data is fetched.
const createResourceAtom = <Response>(
  href: string | undefined,
  refetchOptions:
    | 'keep-old-data-on-refetch'
    | 'clear-old-data-on-refetch' = 'clear-old-data-on-refetch'
) => {
  // If a test pre-seeded this href, hydrate the atom's initial value synchronously so
  // the resource is resolved on the first commit (mirrors the `rootStore` fallback in
  // `rootResourceAtom`). Empty in production, so behaviour there is unchanged.
  const seeded =
    href !== undefined && resourceSeedStore.has(href)
      ? (resourceSeedStore.get(href) as Response)
      : undefined;
  const innerAtom = atom<{
    loading: boolean;
    data: Response | { error: string } | undefined;
  }>(
    seeded !== undefined
      ? { loading: false, data: seeded }
      : { loading: true, data: undefined }
  );
  const abortController = new AbortController();
  const resourceAtom = atom(
    (get) => get(innerAtom),
    async (get, set) => {
      if (!href) return;
      set(innerAtom, {
        loading: true,
        data:
          refetchOptions === 'keep-old-data-on-refetch'
            ? get(innerAtom).data
            : undefined,
      });

      const data = await request.get<Response>(href, abortController);
      set(innerAtom, { loading: false, data });
    }
  );
  resourceAtom.onMount = (set) => {
    // When pre-seeded (test-only), skip the automatic on-mount fetch: the atom already
    // holds resolved data, so fetching would flip it back to `{ loading: true }` until
    // the async response lands — re-introducing the very async gate the seed removes.
    // The setter stays intact, so explicit refetches (e.g. `getDevArea()`) still work.
    if (seeded === undefined) set();
    return () => abortController.abort();
  };

  return resourceAtom;
};

const atomWithSessionStorage = <T>(
  key: string,
  initialValue: T,
  options?: { getOnInit?: boolean }
) => {
  const storage = createJSONStorage<T>(() => sessionStorage);
  return atomWithStorage(key, initialValue, storage, options);
};

const atomWithLoadable = <T>(read: (get: Getter) => Promise<T>) => {
  const baseAtom = atom(read);
  const loadableAtom = loadable(baseAtom);
  return [baseAtom, loadableAtom] as const;
};

// TODO: lazyAtom/lazyAtomWithLoadable/lazyLoadableAtom are "delayed reactive" - they prevent
// auto-execution on mount, but AFTER the first trigger they become fully reactive to dependencies.
// This is misleading because "lazy" implies "only when I ask". Consider:
// 1. Renaming to `delayedReactiveAtom` for clarity
// 2. For "fire only on trigger" behavior, use `mutationAtom`/`mutationAtomWithLoadable` instead
/**
 * Creates a lazy atom that only evaluates when explicitly triggered.
 *
 * Unlike regular atoms that evaluate on first read, lazyAtom returns null
 * until the setter is called. Useful for event-triggered fetching.
 *
 * NOTE: After the first trigger, this atom becomes reactive to its dependencies.
 * If you need an atom that ONLY fires on explicit trigger (e.g., mutations),
 * use `mutationAtom` or `mutationAtomWithLoadable` instead.
 *
 * @param {Function} read - The read function that returns T or Promise<T>
 * @return {WritableAtom} A writable atom that triggers evaluation when set
 * @example
 * const userDataAtom = lazyAtom(async (get) => {
 *   const userId = get(userIdAtom);
 *   return await request.get(`/api/users/${userId}`);
 * });
 *
 * // In component
 * const [data, fetch] = useAtom(userDataAtom);
 * // data is null until fetch() is called
 * <button onClick={() => fetch()}>Load</button>
 */
const lazyAtom = <T>(read: (get: Getter) => T | Promise<T>) => {
  const triggerAtom = atom<number | null>(null);

  const asyncAtom = atom((get) => {
    const trigger = get(triggerAtom);
    if (trigger === null) {
      return null;
    }
    return read(get);
  });

  return atom(
    (get) => get(asyncAtom),
    (_get, set) => set(triggerAtom, Date.now())
  );
};

/**
 * Creates a lazy atom wrapped with loadable() for loading/error states.
 *
 * @param {Function} read - The read function that returns T or Promise<T>
 * @return {WritableAtom} A writable atom with loadable state that triggers evaluation when set
 * @example
 * const userDataAtom = lazyLoadableAtom(async (get) => {
 *   const userId = get(userIdAtom);
 *   return await request.get(`/api/users/${userId}`);
 * });
 *
 * // In component
 * const [state, fetch] = useAtom(userDataAtom);
 * // state: { state: 'hasData', data: null } (idle)
 * // state: { state: 'loading' } (fetching)
 * // state: { state: 'hasData', data: T } (success)
 * // state: { state: 'hasError', error: Error } (error)
 * <button onClick={() => fetch()}>Load</button>
 */
const lazyLoadableAtom = <T>(read: (get: Getter) => T | Promise<T>) => {
  const triggerAtom = atom<number | null>(null);

  const asyncAtom = atom((get) => {
    const trigger = get(triggerAtom);
    if (trigger === null) {
      return null;
    }
    return read(get);
  });

  const loadableAtom = loadable(asyncAtom);

  return atom(
    (get) => get(loadableAtom),
    (_get, set) => set(triggerAtom, Date.now())
  );
};

/**
 * Creates a lazy atom and returns both base and loadable versions.
 * Both share the same trigger - calling either setter triggers the fetch.
 *
 * @param {Function} read - The read function that returns T or Promise<T>
 * @return {Array} A tuple of [baseAtom, loadableAtom]
 * @example
 * const [createTractAtom, createTractLoadableAtom] = lazyAtomWithLoadable(
 *   async (get) => {
 *     const form = await get(formAtom);
 *     return await request.post('/api/tracts', form);
 *   }
 * );
 *
 * // Use base atom for raw value
 * const [response, trigger] = useAtom(createTractAtom);
 *
 * // Use loadable atom for loading/error states
 * const [state, trigger] = useAtom(createTractLoadableAtom);
 */
const lazyAtomWithLoadable = <T>(read: (get: Getter) => T | Promise<T>) => {
  const triggerAtom = atom<number | null>(null);

  const asyncAtom = atom((get) => {
    const trigger = get(triggerAtom);
    if (trigger === null) {
      return null;
    }
    return read(get);
  });

  const loadableAsyncAtom = loadable(asyncAtom);

  const baseAtom = atom(
    (get) => get(asyncAtom),
    (_get, set) => set(triggerAtom, Date.now())
  );

  const loadableAtom = atom(
    (get) => get(loadableAsyncAtom),
    (_get, set) => set(triggerAtom, Date.now())
  );

  return [baseAtom, loadableAtom] as const;
};

/**
 * Creates a mutation atom that only executes when explicitly triggered.
 *
 * Unlike lazy atoms where logic is in the getter (reactive to dependencies),
 * mutation atoms have logic in the setter (only runs on explicit call).
 * This prevents re-execution when dependencies change.
 *
 * @param {Function} mutate - The mutation function that returns Promise<T>
 * @return {WritableAtom} A writable atom that executes mutation when set
 * @example
 * const submitFormAtom = mutationAtom(async (get, set) => {
 *   const form = get(formAtom);
 *   return await request.post('/api/submit', form);
 * });
 *
 * // In component
 * const [result, submit] = useAtom(submitFormAtom);
 * // result is null until submit() is called
 * // After submit(), result is Promise<T> (suspends until resolved)
 *
 * // With arguments:
 * const fetchAtom = mutationAtom(async (get, set, href: string) => {
 *   return await request.get(href);
 * });
 * const [result, fetch] = useAtom(fetchAtom);
 * fetch('/api/data'); // pass href as argument
 */
const mutationAtom = <T, Args extends unknown[] = []>(
  mutate: (get: Getter, set: Setter, ...args: Args) => Promise<T>
) => {
  const promiseAtom = atom<Promise<T> | null>(null);

  return atom(
    (get) => get(promiseAtom),
    (get, set, ...args: Args) => {
      const promise = mutate(get, set, ...args);
      set(promiseAtom, promise);
      return promise;
    }
  );
};

/**
 * Creates a mutation atom and returns both base and loadable versions.
 * Both share the same state - calling either setter triggers the mutation.
 *
 * Unlike lazy atoms where logic is in the getter (reactive to dependencies),
 * mutation atoms have logic in the setter (only runs on explicit call).
 * This prevents re-execution when dependencies change.
 *
 * @param {Function} mutate - The mutation function that returns Promise<T>
 * @return {Array} A tuple of [baseAtom, loadableAtom]
 * @example
 * const [submitFormAtom, submitFormLoadableAtom] = mutationAtomWithLoadable(
 *   async (get, set) => {
 *     const form = get(formAtom);
 *     return await request.post('/api/submit', form);
 *   }
 * );
 *
 * // Use base atom (suspends on pending promise)
 * const [result, submit] = useAtom(submitFormAtom);
 *
 * // Use loadable atom for loading/error states
 * const [state, submit] = useAtom(submitFormLoadableAtom);
 * // state: { state: 'hasData', data: null } (before trigger)
 * // state: { state: 'loading' } (pending)
 * // state: { state: 'hasData', data: T } (success)
 * // state: { state: 'hasError', error: Error } (error)
 *
 * // With arguments:
 * const [fetchAtom, fetchLoadableAtom] = mutationAtomWithLoadable(
 *   async (get, set, href: string) => {
 *     return await request.get(href);
 *   }
 * );
 * const [state, fetch] = useAtom(fetchLoadableAtom);
 * fetch('/api/data'); // pass href as argument
 */
const mutationAtomWithLoadable = <T, Args extends unknown[] = []>(
  mutate: (get: Getter, set: Setter, ...args: Args) => Promise<T>
) => {
  const promiseAtom = atom<Promise<T> | null>(null);

  const doMutate = (get: Getter, set: Setter, ...args: Args) => {
    const promise = mutate(get, set, ...args);
    set(promiseAtom, promise);
    return promise;
  };

  const baseAtom = atom((get) => get(promiseAtom), doMutate);

  const loadableBaseAtom = loadable(baseAtom);
  const loadableAtom = atom((get) => get(loadableBaseAtom), doMutate);

  return [baseAtom, loadableAtom] as const;
};

export {
  createResourceCacheMapAtom,
  createResourceAtom,
  createSingleCacheEntryAtom,
  seedResource,
  clearSeededResources,
  atomWithSessionStorage,
  atomWithLoadable,
  lazyAtom,
  lazyLoadableAtom,
  lazyAtomWithLoadable,
  mutationAtom,
  mutationAtomWithLoadable,
};
